import { distanceKmBetween, etaMinutesForDistance, type Coordinates } from "./dispatch-client";
import { nextJobStep } from "./job-security";

export const FLOW_STEPS = ["Booked", "Coming", "Arrived", "Working", "Pay"] as const;

export function liveReach(you: Coordinates | null, other: Coordinates | null) {
  if (!you || !other) return null;
  const km = Math.round(distanceKmBetween(you, other) * 10) / 10;
  const etaMin = etaMinutesForDistance(km);
  return {
    km,
    etaMin,
    distanceLabel: `${km} km`,
    etaLabel: `~${etaMin} min`,
  };
}

export function flowStepIndex(status: string, payCollected: boolean) {
  if (status === "completed") return payCollected ? 5 : 4;
  if (status === "in_progress") return 3;
  if (status === "arrived") return 2;
  if (status === "en_route" || status === "confirmed") return 1;
  return 0;
}

type Guide = {
  step: number;
  total: number;
  kicker: string;
  title: string;
  detail: string;
  flash: boolean;
  showPayment: boolean;
  showCodeMini: boolean;
  codeVerified: boolean;
};

export type { Guide };

export function customerGuide(status: string, payCollected: boolean, hasCode: boolean): Guide {
  const step = flowStepIndex(status, payCollected);
  if (status === "partner_accepted") {
    return {
      step: 1, total: 5, kicker: "NEXT STEP", title: "Agree the locked price",
      detail: "Tap Agree to unlock contact & live map.", flash: true,
      showPayment: false, showCodeMini: false, codeVerified: false,
    };
  }
  if (status === "completed" && !payCollected) {
    return {
      step: 5, total: 5, kicker: "PAY NOW", title: "Pay your mistri in cash or UPI",
      detail: "Only tap “I paid” after you hand over the money.", flash: true,
      showPayment: true, showCodeMini: hasCode, codeVerified: true,
    };
  }
  if (status === "completed" && payCollected) {
    return {
      step: 5, total: 5, kicker: "ALL DONE", title: "Trip complete — thank you!",
      detail: "Payment recorded. Rate your experience in support if needed.", flash: false,
      showPayment: false, showCodeMini: hasCode, codeVerified: true,
    };
  }
  if (status === "in_progress") {
    return {
      step: 4, total: 5, kicker: "WORKING", title: "Mistri is fixing your vehicle",
      detail: "Stay nearby. Pay only after job is marked done.", flash: false,
      showPayment: false, showCodeMini: hasCode, codeVerified: true,
    };
  }
  if (status === "arrived") {
    return {
      step: 3, total: 5, kicker: "ARRIVED", title: "Mistri is at your location",
      detail: hasCode ? "Share your 4-digit code when mistri asks." : "Mistri will start work shortly.", flash: false,
      showPayment: false, showCodeMini: hasCode, codeVerified: false,
    };
  }
  return {
    step: 2, total: 5, kicker: "ON THE WAY", title: "Mistri is coming to you",
    detail: "Live distance & ETA update below the map.", flash: false,
    showPayment: false, showCodeMini: hasCode, codeVerified: false,
  };
}

export function partnerGuide(status: string, payCollected: boolean, hasCode: boolean): Guide {
  const next = nextJobStep(status);
  if (status === "partner_accepted") {
    return {
      step: 1, total: 5, kicker: "WAITING", title: "Customer must agree price",
      detail: "Numbers unlock after they tap Agree on their phone.", flash: false,
      showPayment: false, showCodeMini: false, codeVerified: false,
    };
  }
  if (status === "completed" && !payCollected) {
    return {
      step: 5, total: 5, kicker: "COLLECT PAYMENT", title: "Job done — collect ₹ from customer",
      detail: "Tap only after customer pays you in cash or UPI.", flash: true,
      showPayment: true, showCodeMini: hasCode, codeVerified: true,
    };
  }
  if (status === "completed" && payCollected) {
    return {
      step: 5, total: 5, kicker: "DONE", title: "Payment received — great job!",
      detail: "Send platform fee from earnings tab when ready.", flash: false,
      showPayment: false, showCodeMini: hasCode, codeVerified: true,
    };
  }
  const labels: Record<string, { title: string; detail: string }> = {
    en_route: { title: "Tap “On the way”", detail: "Let customer know you left." },
    arrived: { title: "Tap “Arrived” + enter customer code", detail: "Ask customer for their 4-digit code." },
    in_progress: { title: "Tap “Start work” + enter code", detail: "Begin repair after code matches." },
    completed: { title: "Tap “Job done”", detail: "Only after work is finished." },
  };
  const label = next ? labels[next] : { title: "Follow the steps below", detail: "" };
  return {
    step: flowStepIndex(status, payCollected),
    total: 5,
    kicker: "YOUR NEXT TAP",
    title: label.title,
    detail: label.detail,
    flash: !!next,
    showPayment: false,
    showCodeMini: hasCode,
    codeVerified: status === "in_progress" || status === "completed",
  };
}
